<template>
  <div class="p-contentplans-writing-serps-titles-item">
    <div
      class="p-contentplans-writing-serps-titles-item__rank"
      :class="intent && 'has-intent is-' + intent.name"
    >
      <div v-if="item.isOwnSite" class="own-site">登録サイト</div>
      <div>{{ rankLabel }}</div>
      <div
        v-if="intent && intent.label"
        class="p-contentplans-writing-serps-titles-item__intent"
      >
        {{ intent.label }}
        <div class="unknown-intent-message" v-if="showUnknownTooltip">
          <Tooltip size="sm">
            タイトルが日本語・英語・数字以外の特殊文字のみのため、インテントが不明となっています。
          </Tooltip>
        </div>
      </div>
    </div>
    <div class="p-contentplans-writing-serps-titles-item__content">
      <div
        v-if="isOutOfRank"
        class="p-contentplans-writing-serps-titles-item__row"
      >
        <div>100位以内に登録サイトのデータが存在しません。</div>
      </div>
      <template v-else-if="isPdf">
        <div class="p-contentplans-writing-serps-titles-item__row">
          <div class="p-contentplans-writing-serps-titles-item__label">
            タイトル
          </div>
          <div class="p-contentplans-writing-serps-titles-item__title">
            PDFのため分析ができません。
          </div>
        </div>
        <div class="p-contentplans-writing-serps-titles-item__url">
          <a :href="item.url" target="_blank" class="c-link--blank">{{
            item.url
          }}</a>
        </div>
        <div class="p-contentplans-writing-serps-titles-item__row">
          <div class="p-contentplans-writing-serps-titles-item__label">
            ディスクリプション
          </div>
          <div class="p-contentplans-writing-serps-titles-item__description">
            PDFのため分析ができません。
          </div>
        </div>
      </template>
      <template v-else>
        <div class="p-contentplans-writing-serps-titles-item__row">
          <div class="p-contentplans-writing-serps-titles-item__label">
            タイトル
          </div>
          <div class="p-contentplans-writing-serps-titles-item__title">
            <template v-if="isLoadingTitle">タイトルを取得中です。</template>
            <template v-else-if="isEmptyTitle"
              >タイトルが指定されていません。</template
            >
            <template v-else-if="isErrorTitle">
              タイトルを取得できませんでした。
              <Tooltip variant="attention">
                データの取得ができませんでした。
                原因は対象のサイトでIPアドレスのブロックなどの理由でデータを取得できなかったことが考えられます。
              </Tooltip>
            </template>
            <template v-else-if="isOverTitleMaxLength">
              {{ item.title.substr(0, MAX_TDH_STRING_LENGTH) }}...<span
                class="c-text--neutral"
                >{{
                  `(※${MAX_TDH_STRING_LENGTH + 1}文字以上は表示しません)`
                }}</span
              >
            </template>
            <template v-else>
              {{ item.title }}
            </template>
          </div>
        </div>
        <div class="p-contentplans-writing-serps-titles-item__url">
          <a :href="item.url" target="_blank" class="c-link--blank">{{
            item.url
          }}</a>
        </div>
        <div class="p-contentplans-writing-serps-titles-item__row">
          <div class="p-contentplans-writing-serps-titles-item__label">
            ディスクリプション
          </div>
          <div class="p-contentplans-writing-serps-titles-item__description">
            <template v-if="isLoadingDescription"
              >ディスクリプションを取得中です。</template
            >
            <template v-else-if="isEmptyDescription">
              ディスクリプションが指定されていません。
            </template>
            <template v-else-if="isErrorDescription">
              <p>ディスクリプションが取得できませんでした。</p>
              <Tooltip variant="attention">
                データの取得ができませんでした。
                原因は対象のサイトでIPアドレスのブロックなどの理由でデータを取得できなかったことが考えられます。
              </Tooltip>
            </template>
            <template v-else-if="isOverDescriptionMaxLength">
              {{ item.description.substr(0, MAX_TDH_STRING_LENGTH) }}...<span
                class="c-text--neutral"
                >{{
                  `(※${MAX_TDH_STRING_LENGTH + 1}文字以上は表示しません)`
                }}</span
              >
            </template>
            <template v-else>
              {{ item.description }}
            </template>
          </div>
        </div>
        <div
          v-if="isLoadingHeadings || isErrorHeadings"
          class="p-contentplans-writing-serps-titles-item__row"
        >
          <div v-if="isLoadingHeadings">ページの見出しタグを取得中です。</div>
          <div v-else-if="isErrorHeadings">
            ページの中に見出しタグはみつかりませんでした。
            <Tooltip variant="attention" position="left-top">
              データの取得ができませんでした。
              原因は対象のサイトでIPアドレスのブロックなどの理由でデータを取得できなかったことが考えられます。
            </Tooltip>
          </div>
        </div>
        <ul v-else class="p-contentplans-writing-serps-headings-list">
          <SerpsTitlesListItemHeadings
            v-for="(header, index) in item.headerStructures"
            :key="index"
            :errorAddHeading="errorAddHeading"
            :item="header"
            :isEditMode="isEditMode"
            @addHeading="addHeading"
          />
        </ul>
      </template>
    </div>
  </div>
</template>
<script>
import { INTENT_TYPES } from '@/constants';
import SerpsTitlesListItemHeadings from '@/components/contentPlans/writing/SerpsTitlesListItemHeadings';
import Tooltip from '@/components/Tooltip';
import { MAX_TDH_STRING_LENGTH } from '@/constants';

export default {
  props: ['errorAddHeading', 'item', 'project', 'index', 'isEditMode'],
  components: {
    SerpsTitlesListItemHeadings,
    Tooltip
  },
  computed: {
    intent() {
      if (this.item.intent && this.item.intent.label) {
        return INTENT_TYPES[this.item.intent.label];
      }
      return null;
    },
    showUnknownTooltip() {
      if (this.item.intent.label === INTENT_TYPES.unknown.name) {
        return true;
      }
      return false;
    },
    rankLabel() {
      return this.isOutOfRank ? '圏外' : this.item.rank;
    },
    isPdf() {
      return this.item.contentType === 'pdf';
    },
    isOutOfRank() {
      return !this.item.url && this.item.isOwnSite;
    },
    isLoadingTitle() {
      return this.item.title === null;
    },
    isEmptyTitle() {
      return this.item.title === '';
    },
    isErrorTitle() {
      return typeof this.item.title === 'undefined';
    },
    isLoadingDescription() {
      return this.item.description === null;
    },
    isEmptyDescription() {
      return this.item.description === '';
    },
    isErrorDescription() {
      return typeof this.item.description === 'undefined';
    },
    isLoadingHeadings() {
      return (
        this.item.headerStructures === null ||
        (this.item.headerStructures.length === 0 &&
          this.item.description === null)
      );
    },
    isErrorHeadings() {
      return (
        !this.item.headerStructures || this.item.headerStructures.length === 0
      );
    },
    isOverTitleMaxLength() {
      if (this.item.title) {
        return this.item.title.length > MAX_TDH_STRING_LENGTH;
      }
      return false;
    },
    isOverDescriptionMaxLength() {
      if (this.item.description) {
        return this.item.description.length > MAX_TDH_STRING_LENGTH;
      }
      return false;
    },
    MAX_TDH_STRING_LENGTH() {
      return MAX_TDH_STRING_LENGTH;
    }
  },
  methods: {
    addHeading(level, value) {
      this.$emit('addHeading', level, value);
    }
  }
};
</script>
